import { MockedAuthManager } from "../../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing/react";
import React, { useEffect, useMemo, useRef } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { AssetStatus, ContractSignatureStatus, ProfileType } from "../../../__generated__/graphql";
import { GET_CONTRACT } from "../../../graphql/core";
import {
    buildAsset,
    buildClientInfo,
    buildClientProfile,
    buildContract,
    buildMissionInfo,
    buildUser,
} from "../../factories";
import type { Meta, StoryObj } from "@storybook/react";
import { MockLink } from "@apollo/client/testing";
import CreateContractPreSign from "../../../screens/create-mission/CreateContractPreSign";
import { useNavigation } from "@react-navigation/native";
import { Button, View } from "react-native";
import Callout from "../../../ui_atoms/Callout";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import { GET_ASSET } from "../../../graphql/asset";
import { StoryStackNavigatorContainer } from "../../display-helpers";
import { ContractType } from "../../../types";

type WithNavigationProps = {
    navigation: {
        reset: (x: object) => void;
    };
};

const HomeFakeScreen: React.FC<WithNavigationProps> = ({ navigation }) => {
    return (
        <View>
            <Callout markdown="Home screen" />
            <Button
                title="Sign contract"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "CreateContractPreSign",
                                params: { contractId: "test-contract" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

type FakePreviewScreenProps = WithNavigationProps & {
    route: {
        params: {
            contractId: string;
        };
    };
};

const FakePreviewScreen: React.FC<FakePreviewScreenProps> = ({ route, navigation }) => {
    return (
        <View>
            <Callout markdown={`PDF Preview ${route.params.contractId}`} />
            <Button
                title="Sign contract"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [{ name: "Home" }],
                    });
                }}
            />
        </View>
    );
};
type StoryStackParams = {
    CreateContractPreSign: { contractType?: ContractType; contractId: string };
    Home: undefined;
    ContractPreviewScreen: { contractId: string };
};

type StoryStackNavigatorParams = StackScreenProps<StoryStackParams>;

const Stack = createNativeStackNavigator<StoryStackParams>();

const StoryComponent = ({ state }: { state: ContractSignatureStatus }) => {
    const user = useMemo(
        () =>
            buildUser({
                clientInfos: [buildClientInfo({ id: "client" })],
                clientProfiles: [
                    buildClientProfile({
                        id: "client",
                    }),
                ],
            }),
        [],
    );
    const assetStatusRef = useRef(AssetStatus.Pending);
    const mock = useMemo(
        () =>
            Object.values(ContractSignatureStatus)
                // Mock callbacks read/write `assetStatusRef.current` when the storybook
                // user triggers the flow; they don't run during render.
                // eslint-disable-next-line react-hooks/refs
                .reduce((acc, state) => {
                    const contractId = `test-contract-${state}`;
                    acc.push({
                        request: {
                            query: GET_CONTRACT,
                            variables: {
                                id: contractId,
                            },
                        },
                        result: () => {
                            assetStatusRef.current = AssetStatus.Pending;
                            setTimeout(() => {
                                assetStatusRef.current = AssetStatus.Ready;
                            }, 8000);
                            return {
                                data: {
                                    getContract: {
                                        ...buildContract({
                                            id: contractId,
                                            signatureStatus: state,
                                        }),
                                        ...{ missionInfo: buildMissionInfo() },
                                    },
                                },
                            };
                        },
                    });
                    return acc;
                }, [] as MockLink.MockedResponse[])
                .concat([
                    {
                        request: {
                            query: GET_ASSET,
                            variables: () => true,
                        },
                        result: () => {
                            return {
                                data: {
                                    getAsset: buildAsset({
                                        status: assetStatusRef.current,
                                    }),
                                },
                            };
                        },
                    },
                ]),
        [],
    );

    const navigation = useNavigation<StoryStackNavigatorParams["navigation"]>();

    useEffect(() => {
        navigation.navigate("CreateContractPreSign", { contractId: `test-contract-${state}` });
    }, [state, navigation]);

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Client}>
                <MockedProvider mocks={mock}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="CreateContractPreSign">
                            <Stack.Screen
                                name="CreateContractPreSign"
                                component={CreateContractPreSign}
                                initialParams={{ contractId: `test-contract-${state}` }}
                                options={{
                                    gestureEnabled: false,
                                    headerShown: true,
                                    title: "bliblablo",
                                    headerBackTitle: "Back",
                                    headerBackButtonDisplayMode: "default",
                                }}
                            />
                            <Stack.Screen name="Home" component={HomeFakeScreen} />
                            <Stack.Screen
                                name="ContractPreviewScreen"
                                component={FakePreviewScreen}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "CreateMission/Pre Sign",
    component: StoryComponent,
    argTypes: {
        state: {
            options: Object.values(ContractSignatureStatus),
            control: { type: "radio" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const NeedSelfSign: Story = {
    name: "NeedSelfSign",
    args: {
        state: ContractSignatureStatus.NeedSelfSign,
    },
};

export const Success: Story = {
    name: "Success",
    args: {
        state: ContractSignatureStatus.Success,
    },
};

export const WaitingContract: Story = {
    name: "WaitingContract",
    args: {
        state: ContractSignatureStatus.WaitingContract,
    },
};
